/**
 * ProfilePage
 *
 * Main container for the profile view with tabs:
 * - Calendario (CalendarDays icon)
 * - Workload (BarChart3 icon)
 * - Impostazioni (Settings icon) - only for own profile
 */

import { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarDays, BarChart3, Settings, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '@/context/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useAppStore } from '@/store/appStore';
import { useProfilesContext } from '@/context/ProfilesContext';
import { supabase } from '@/lib/supabaseClient';
import { ProfileHeader } from './ProfileHeader';
import { ProfileCalendar } from './ProfileCalendar';
import { ProfileWorkload } from './ProfileWorkload';
import { ProfileSettings } from './ProfileSettings';
import { ProfileShareDialog } from './ProfileShareDialog';

type ProfileTab = 'calendario' | 'workload' | 'impostazioni';

export function ProfilePage() {
  const { userId: paramUserId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const setSelectedTaskId = useAppStore((state) => state.setSelectedTaskId);

  const targetUserId =
    !paramUserId || paramUserId === 'me' ? user?.id : paramUserId;

  const {
    profileSettings,
    isLoading,
    isOwnProfile,
    updateProfileSettings,
    generateShareLink,
    userTasks,
    workloadStats,
  } = useProfile(targetUserId);

  const { resolveDisplayName, getProfile } = useProfilesContext();
  const [activeTab, setActiveTab] = useState<ProfileTab>('calendario');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Fallback profile fetch for users not in current org (former members)
  const [fallbackProfile, setFallbackProfile] = useState<{ display_name?: string; email?: string; avatar_url?: string } | null>(null);
  useEffect(() => {
    if (isOwnProfile || !targetUserId) return;
    const cached = getProfile(targetUserId);
    if (cached) return;
    // Fetch from org_members or auth metadata
    let cancelled = false;
    supabase
      .from('org_members')
      .select('display_name, email, avatar_url')
      .eq('user_id', targetUserId)
      .limit(1)
      .then(({ data }) => {
        if (!cancelled && data?.[0]) setFallbackProfile(data[0]);
      });
    return () => { cancelled = true; };
  }, [targetUserId, isOwnProfile, getProfile]);

  // Resolve profile: context first, then fallback
  const resolvedProfile = getProfile(targetUserId || '') || fallbackProfile;

  // Get user info
  const userName = useMemo(() => {
    if (isOwnProfile) {
      return user?.user_metadata?.full_name || 'Utente';
    }
    if (targetUserId) {
      const name = resolveDisplayName(targetUserId);
      if (name !== targetUserId) return name;
      return resolvedProfile?.display_name || resolvedProfile?.email || 'Utente';
    }
    return 'Utente';
  }, [isOwnProfile, user, targetUserId, resolveDisplayName, resolvedProfile]);

  const userEmail = useMemo(() => {
    if (isOwnProfile) {
      return user?.email || '';
    }
    return resolvedProfile?.email || '';
  }, [isOwnProfile, user, resolvedProfile]);

  const avatarUrl = useMemo(() => {
    if (isOwnProfile) {
      return user?.user_metadata?.avatar_url;
    }
    return resolvedProfile?.avatar_url;
  }, [isOwnProfile, user, resolvedProfile]);

  const tabs: { id: ProfileTab; label: string; icon: typeof CalendarDays; show: boolean }[] = [
    { id: 'calendario', label: 'Calendario', icon: CalendarDays, show: true },
    { id: 'workload', label: 'Workload', icon: BarChart3, show: true },
    { id: 'impostazioni', label: 'Impostazioni', icon: Settings, show: isOwnProfile },
  ];

  const handleTaskClick = (task: { id: string }) => {
    setSelectedTaskId(task.id);
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-surface-primary">
        <div className="flex flex-col items-center gap-3 animate-fade-in">
          <Loader2 className="w-6 h-6 text-accent-violet animate-spin" />
          <p className="text-[12px] text-text-tertiary">Caricamento profilo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-surface-primary animate-fade-in">
      {/* Header */}
      <ProfileHeader
        userId={targetUserId || ''}
        userName={userName}
        userEmail={userEmail}
        avatarUrl={avatarUrl}
        isOwnProfile={isOwnProfile}
        onShare={() => setShareDialogOpen(true)}
      />

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 px-6 pt-3 pb-0 border-b border-border-primary bg-surface-secondary/20">
        {tabs
          .filter((t) => t.show)
          .map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={clsx(
                'flex items-center gap-2 px-3.5 py-2.5 text-[12px] font-medium border-b-2 transition-all -mb-px',
                activeTab === id
                  ? 'text-accent-violet border-accent-violet'
                  : 'text-text-tertiary border-transparent hover:text-text-secondary hover:border-border-secondary'
              )}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'calendario' && (
          <ProfileCalendar tasks={userTasks} onTaskClick={handleTaskClick} />
        )}
        {activeTab === 'workload' && (
          <ProfileWorkload stats={workloadStats} tasks={userTasks} />
        )}
        {activeTab === 'impostazioni' && isOwnProfile && (
          <ProfileSettings
            settings={profileSettings}
            onUpdate={updateProfileSettings}
          />
        )}
      </div>

      {/* Share Dialog */}
      <ProfileShareDialog
        isOpen={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        settings={profileSettings}
        onUpdate={updateProfileSettings}
        onGenerateLink={generateShareLink}
      />
    </div>
  );
}
